import { useUndoRedo } from "$root/hooks/useHistory";
import { seconds } from "$root/utils/time";
import type { Currencies, LabChartTypeEnum, LabComputeEvent } from "@eu.mdotm.api/portfolio-lab";
import type { ParameterizedConceptDTO } from "@eu.mdotm.api/smart-workspaces";
import type { InvestmentStatus, UserInstrumentDto } from "@eu.mdotm.api/sphere";
import { atom, atomWithCompare, type RwAtom } from "@mdotm/shadcn/legacy/reactivity";
import type { Map as ImmutableMap } from "immutable";
import { createContext, useMemo, useState, type ReactNode } from "react";
import type { ColumnState } from "ag-grid-community";
import type { CompositionGroupBy } from "../state";

export type PortfolioLabInvestmentType = "INVESTMENT" | "REFERENCE" | "BENCHMARK" | "INVESTMENT_PROPOSAL";

/**
 * Single-flight, last-write-wins serializer for the lab-session view-config PUT. Session saves have no
 * ordering relationship with virtual-investment mutations, so they get their own lane, separate from
 * the virtual-investment write lanes. While a save is in flight, a newer trigger only remembers the LATEST write
 * closure; when the running save settles that closure runs and (reading live editor state) pushes the
 * current config — so the server converges on the newest state without piling up redundant PUTs.
 */
export type LabSessionSaveChain = { run(write: () => Promise<void>): void };

function makeLabSessionSaveChain(): LabSessionSaveChain {
	let running = false;
	let next: (() => Promise<void>) | null = null;
	async function pump(): Promise<void> {
		if (running || next == null) {
			return;
		}
		running = true;
		const write = next;
		next = null;
		try {
			await write();
		} finally {
			running = false;
			void pump();
		}
	}
	return {
		run(write) {
			next = write;
			void pump();
		},
	};
}

/**
 * Per-investment write serializer for virtual-investment mutations (create/update/delete/reset/
 * real-commit). These writes have a per-investment ordering requirement — composition edits must
 * reach the server in the order the user made them (T6), and the reset→re-push resync pairing must
 * not interleave — but NO cross-investment ordering relationship. So each `virtualUuid` gets its own
 * promise-chain lane: same-uuid writes run strictly in submission order; different-uuid writes run on
 * independent lanes with no coupling, so unrelated investments never block each other.
 *
 * The metrics compute does not run here: the backend discards a compute whose revision moved, so it
 * needs no ordering against these writes (see `reloadInvestmentCharts`).
 *
 * react-query v4 has no `scope.id` mutation serialization, so this is hand-rolled. A lane's tail
 * promise never rejects (a failed write must not wedge the writes chained behind it), and a
 * lane is dropped once drained so the map can't grow unbounded across a session. The returned
 * promise mirrors the write's own settlement, so a caller may await it or fire-and-forget.
 */
export type InvestmentWriteLanes = { run<T>(virtualUuid: string, write: () => Promise<T>): Promise<T> };

function makeInvestmentWriteLanes(): InvestmentWriteLanes {
	const tails = new Map<string, Promise<unknown>>();
	return {
		run(virtualUuid, write) {
			const prev = tails.get(virtualUuid) ?? Promise.resolve();
			const result = prev.then(() => write());
			// The lane tail swallows rejections (a failed write must not block the writes chained behind it) and
			// settles only after `result` does, so same-uuid writes stay strictly ordered regardless of
			// whether the preceding one succeeded.
			const tail = result.catch(() => undefined);
			tails.set(virtualUuid, tail);
			void tail.then(() => {
				// Drop the lane once fully drained so the map doesn't accumulate dead uuids over a session.
				if (tails.get(virtualUuid) === tail) {
					tails.delete(virtualUuid);
				}
			});
			return result;
		},
	};
}

/**
 * Per-investment abort registry for the `/compute` request, which only queues the column's compute
 * (its outcome arrives as a `lab-compute` event, see `pendingCompute`).
 *
 * It exists so a deleted column stops queuing: the charts a compute would refresh are going away
 * with the column. A uuid can hold several requests at once (the load-time one and an "Update
 * Charts" click), and `abort` cancels all of them.
 */
export type InvestmentComputes = {
	/** Runs `task` with a per-uuid abort signal registered for its duration, so `abort` can cancel it. */
	run<T>(virtualUuid: string, task: (signal: AbortSignal) => Promise<T>): Promise<T>;
	/** Aborts every compute currently registered for `virtualUuid`; a no-op when none is in flight. */
	abort(virtualUuid: string): void;
};

function makeInvestmentComputes(): InvestmentComputes {
	const controllers = new Map<string, Set<AbortController>>();
	return {
		async run(virtualUuid, task) {
			const controller = new AbortController();
			const registered = controllers.get(virtualUuid) ?? new Set();
			registered.add(controller);
			controllers.set(virtualUuid, registered);
			try {
				return await task(controller.signal);
			} finally {
				registered.delete(controller);
				if (registered.size === 0 && controllers.get(virtualUuid) === registered) {
					controllers.delete(virtualUuid);
				}
			}
		},
		abort(virtualUuid) {
			for (const controller of controllers.get(virtualUuid) ?? []) {
				controller.abort();
			}
			controllers.delete(virtualUuid);
		},
	};
}

/** How long a `lab-compute` event that matched no pending compute is kept for the answer it beat. */
const EARLY_COMPUTE_EVENT_HOLD_MS = seconds(10);

/**
 * `lab-compute` events that matched no column's pending compute when they arrived, kept by
 * `computeId` for {@link EARLY_COMPUTE_EVENT_HOLD_MS}. The backend queues the runs before it answers
 * `/compute`, so an event can reach the browser before the answer that carries its `computeId`; the
 * answer then takes it from here. Events for computes this tab never queued (another tab, a replaced
 * compute, a deleted column) expire unclaimed.
 */
export type EarlyComputeEvents = {
	hold(event: LabComputeEvent): void;
	/** Removes and returns the held event for `computeId`, if one arrived early. */
	take(computeId: string): LabComputeEvent | undefined;
};

function makeEarlyComputeEvents(): EarlyComputeEvents {
	const held = new Map<string, LabComputeEvent>();
	return {
		hold(event) {
			held.set(event.computeId, event);
			setTimeout(() => {
				if (held.get(event.computeId) === event) {
					held.delete(event.computeId);
				}
			}, EARLY_COMPUTE_EVENT_HOLD_MS);
		},
		take(computeId) {
			const event = held.get(computeId);
			held.delete(computeId);
			return event;
		},
	};
}

export type PortfolioLabCompositionInstrument = {
	type: "instrument";
	tickerName: string;
	weight: number;
	monetaryWeight: number;
	/** A sale: held here at weight 0 and saved as -1, only while the weight stays 0. */
	sold?: true;
};

export type PortfolioLabCompositionPortfolio = {
	type: "portfolio";
	uuid: string;
	name: string;
	weight: number;
	monetaryWeight: number;
	instruments: { tickerName: string; weight: number; monetaryWeight: number; sold?: true }[];
};

export type PortfolioLabComposition = PortfolioLabCompositionInstrument | PortfolioLabCompositionPortfolio;

export type PortfolioLabInvestmentItem = {
	uuid: string;
	virtualUuid: string;
	name: string;
	type: PortfolioLabInvestmentType;
	status: InvestmentStatus;
	/**
	 * Base currency of the source investment (propagated from the API at create time). Drives the
	 * monetary column's currency symbol. `null` when the source carries no currency — the monetary
	 * column then falls back to the lab's default (EUR) symbol.
	 */
	currency: Currencies | null;
	aum: number | null;
	initialAum: number | null;
	initialComposition: PortfolioLabComposition[];
	currentComposition: PortfolioLabComposition[];
	/**
	 * The local `initialComposition` / `initialAum` moved and the server's baseline has not taken it
	 * yet. Set by whatever moves the baseline (Update AUM, undo/redo across one); the next composition
	 * write rebaselines the server first and clears it, so a failed rebaseline is retried by the next
	 * edit instead of being lost.
	 */
	baselineUnsaved: boolean;
	/**
	 * The server's revision of this column's composition and AUM. Moves on every composition write the
	 * server accepts (PUT, reset); a scenario-only or name-only PUT leaves it. Written only by the
	 * composition write helper (`runCompositionWrite`) and by a re-seed from the server's list.
	 */
	compositionRevision: number;
	/**
	 * The `compositionRevision` the stored reports were computed from; `null` when the column has no
	 * reports. Seeded from the server's list and moved by a compute that stored its result.
	 */
	reportsComputedAtRevision: number | null;
	/**
	 * Composition writes submitted for this column and not yet acknowledged. While it is above zero the
	 * server's revision is about to move, so the charts are stale (see `chartsStale`).
	 */
	pendingWrites: number;
	/**
	 * Persistence status of this column's composition. `"saving"` while an "Update composition" commit
	 * to the real investment is in flight (drives the header indicator + disables this column's
	 * weight/monetary editing) and returns to `"idle"` once that request settles. `"error"` when a
	 * virtual-investment sync fails — the local edits are kept (no rollback), the header flags the
	 * divergence, and the next successful sync clears the status back to `"idle"`. A re-read after a bot
	 * write also clears it, taking the server's copy over the failed edits (`useBotWriteResync`).
	 */
	compositionSaveStatus: CompositionSaveStatus;
	/**
	 * Settle state of the last "Update composition" commit. `"waiting"` from commit success until the
	 * real investment's live status settles (SSE-driven): on an updatable status the virtual copy is
	 * re-synced (reset + re-push of the client composition) and charts flip dirty; on `ERROR` the
	 * re-sync is skipped (lab state kept). While `"waiting"` only a SECOND commit is blocked
	 * (`canTriggerUpdateComposition`) — editing the virtual composition stays unlocked.
	 */
	commitSettleStatus: CommitSettleStatus;
	/**
	 * The compute this column waits on, `null` when none is out: one `/compute` queued, whose
	 * `lab-compute` event has not arrived, or one SphereAI's recalculate started, whose task has not
	 * settled. Only the latest is kept, as the backend replaces a queued compute with a newer one of the
	 * same column and never reports the replaced one. See {@link PendingCompute}.
	 */
	pendingCompute: PendingCompute | null;
	/**
	 * The scenario the last compute was asked for when the backend answered `FALLBACK_USED`, i.e.
	 * computed the column over its full history instead; `null` otherwise. Session-only: a reload
	 * seeds it `null` although the stored numbers are still the fallback ones.
	 */
	scenarioFallback: { scenarioId: string } | null;
	/**
	 * The scenario whose window the stored reports cover: the one the last finished compute ran under,
	 * `null` for full history. A reload seeds it from the column's saved scenario, which a scenario-only
	 * save also moves.
	 */
	reportsScenarioId: string | null;
	tickerInfoPool: ImmutableMap<string, UserInstrumentDto>;
};

export type PendingCompute = QueuedCompute | BotCompute;

type PendingComputeTiming = {
	/**
	 * `Date.now()` when the `/compute` answer arrived, or when the chat showed the call; the slow line
	 * and the {@link LAB_COMPUTE_STOP_MS} stop count from it.
	 */
	queuedAt: number;
	/** The scenario it was queued under (`null` for Current Market). */
	scenarioId: string | null;
	/**
	 * Past the {@link LAB_COMPUTE_STOP_MS} stop: the column no longer reads as computing, but its end
	 * still applies when it comes.
	 */
	timedOut: boolean;
};

/** A compute this tab's `/compute` queued; its `lab-compute` event finishes it. */
export type QueuedCompute = PendingComputeTiming & {
	computeId: string;
	/** The composition revision the compute will stamp its reports with. */
	revision: number;
};

/**
 * A compute SphereAI's `portfolio_lab-recalculate` started, seen in this tab's chat (MDOT-25847). The
 * page never learns its `computeId` or revision, so the call's settled task finishes it, not an event.
 */
export type BotCompute = PendingComputeTiming & {
	toolCallId: string;
};

export type CompositionSaveStatus = "idle" | "saving" | "error";
export type CommitSettleStatus = "idle" | "waiting";

export type PortfolioLabChartType = LabChartTypeEnum;

export type PortfolioLabPerformancePreference = { columns: ColumnState[]; showDelta: boolean };
export type PortfolioLabExposurePreference = { firstLevel: string | null; secondLevel: string | null };
export type PortfolioLabTablePreference = { columns: ColumnState[]; showDelta: boolean };

export type PortfolioLabChartPreference =
	| PortfolioLabPerformancePreference
	| PortfolioLabExposurePreference
	| PortfolioLabTablePreference;

type PortfolioLabChartBase<TType extends PortfolioLabChartType, TPreference> = {
	uuid: string;
	name: string;
	type: TType;
	visible: boolean;
	/** Concept uuids to fetch data for (data dimension). Decoupled from `preference` (view: order/hide/width). */
	series: string[];
	preference: TPreference;
};

export type PortfolioLabPerformanceChart = PortfolioLabChartBase<"PERFORMANCE", PortfolioLabPerformancePreference>;
export type PortfolioLabExposureChart = PortfolioLabChartBase<"EXPOSURE", PortfolioLabExposurePreference>;
export type PortfolioLabTableChart = PortfolioLabChartBase<"TABLE", PortfolioLabTablePreference>;

export type PortfolioLabChartItem = PortfolioLabPerformanceChart | PortfolioLabExposureChart | PortfolioLabTableChart;
export type GetPortfolioLabChartItem<TChartType extends PortfolioLabChartType> = Extract<
	PortfolioLabChartItem,
	{ type: TChartType }
>;

export type PageSplitRatio = "1/3" | "1/2" | "2/3";

export type PageSplit = { kind: "ratio"; width: PageSplitRatio } | { kind: "pixels"; width: number };

export type PortfolioLabProps = {
	investments: PortfolioLabInvestmentItem[];
	charts: PortfolioLabChartItem[];
	/**
	 * Session-level basket of all parametrized concepts declared in this Lab. Charts reference these by uuid via
	 * their `series`. The rich {@link ParameterizedConceptDTO} shape (with `source`/`relativeness`) is kept here
	 * because the metrics data query consumes it directly; the lab-session adapter maps it to/from the leaner
	 * persisted `LabParametrizedConcept`.
	 */
	parametrizedConcepts: ParameterizedConceptDTO[];
	pageSplit: PageSplit;
	/** Weight precision digits shown in composition editor (0–4) */
	decimals: number;
	/**
	 * Persisted composition-tree "allocation preference": the group-by selection (allocation vs holdings +
	 * grouped classification) and the grid column layout. Seeds {@link PortfolioLabContextProps.compositionGroupBy$}
	 * and {@link PortfolioLabContextProps.compositionColumnState$} on load. `projection` (the tree's concept
	 * columns) is not FE-driven yet, but is carried through verbatim so a save — a full-replace PUT — never
	 * wipes a value the FE didn't author.
	 */
	grid: { groupBy: CompositionGroupBy; columns: ColumnState[]; projection?: string[] };
};

/**
 * Undo/redo snapshot of everything a composition mutation can touch. The `initial*` baselines are
 * included because Update-AUM (and Update-composition) rebase them — undoing such an action must
 * also restore the previous delta/Reset baseline, or deltas render wrong afterwards (T5).
 */
export type LabHistory = {
	virtualUuid: string;
	composition: PortfolioLabInvestmentItem["currentComposition"];
	aum: PortfolioLabInvestmentItem["aum"];
	initialComposition: PortfolioLabInvestmentItem["initialComposition"];
	initialAum: PortfolioLabInvestmentItem["initialAum"];
};

type MetricsStatus = "success" | "calculating" | "init" | "error";
export type PortfolioLabContextProps = {
	investments$: RwAtom<PortfolioLabProps["investments"]>;
	charts$: RwAtom<PortfolioLabProps["charts"]>;
	parametrizedConcepts$: RwAtom<PortfolioLabProps["parametrizedConcepts"]>;
	pageSplit$: RwAtom<PortfolioLabProps["pageSplit"]>;
	decimals$: RwAtom<number>;
	/**
	 * Composition grid column layout (width/order/pinned/sort) — the grid mirrors every
	 * grid-originated change into this atom via `agExtra.useSyncColStateWithAtom`, making the
	 * latest layout explicit session state that survives editor remounts (e.g. removing the
	 * last investment and loading a new one). `null` until the grid first reports its state.
	 */
	compositionColumnState$: RwAtom<ColumnState[] | null>;
	/**
	 * Composition-tree group-by selection (allocation vs holdings + the grouped classification) — the
	 * "allocation preference". Session state seeded from the persisted lab view config and mirrored back
	 * on change (see useLabSession). Scoped to the lab session, not a module singleton, so it resets
	 * cleanly per mount from the freshly fetched config.
	 */
	compositionGroupBy$: RwAtom<CompositionGroupBy>;
	/**
	 * The composition tree's concept-column projection (`LabGridConfig.projection`). Not FE-driven yet —
	 * held only so it round-trips: seeded from the loaded config and written back unchanged on save, so a
	 * full-replace PUT never discards a projection some other writer persisted. Becomes live state when the
	 * tree-projection feature lands.
	 */
	compositionProjection$: RwAtom<string[] | undefined>;
	selectedScenarioId$: RwAtom<string | null>;
	/**
	 * Last-write-wins lane for the lab-session view-config PUT (see {@link LabSessionSaveChain}). Its own
	 * lane — view-config saves are unordered relative to virtual-investment writes (see
	 * {@link InvestmentWriteLanes}).
	 */
	sessionSaveChain: LabSessionSaveChain;
	/**
	 * Per-investment write serializer for virtual-investment mutations (see {@link InvestmentWriteLanes}).
	 * Each `virtualUuid` has its own ordered lane, so same-investment writes stay ordered while different
	 * investments run independently.
	 */
	investmentWriteLanes: InvestmentWriteLanes;
	/**
	 * Abort registry for the per-investment metrics compute (see {@link InvestmentComputes}), so a
	 * delete can cancel the computes of the column it removes.
	 */
	investmentComputes: InvestmentComputes;
	/** `lab-compute` events waiting for the `/compute` answer they beat (see {@link EarlyComputeEvents}). */
	earlyComputeEvents: EarlyComputeEvents;
	undoRedo: ReturnType<typeof useUndoRedo<LabHistory>>;
	metricsStatus$: RwAtom<MetricsStatus>;
};

export const PortfolioLabContext = createContext<PortfolioLabContextProps | null>(null);

export function PortfolioLabContextProvider(
	props: {
		children: ReactNode;
		/** Seeds {@link PortfolioLabContextProps.selectedScenarioId$} — see `scenarioIdFromColumns`. */
		selectedScenarioId: string | null;
	} & PortfolioLabProps,
) {
	const [
		[
			investments$,
			charts$,
			parametrizedConcepts$,
			pageSplit$,
			decimals$,
			compositionColumnState$,
			compositionGroupBy$,
			compositionProjection$,
			selectedScenarioId$,
			metricsStatus$,
		],
	] = useState<
		[
			PortfolioLabContextProps["investments$"],
			PortfolioLabContextProps["charts$"],
			PortfolioLabContextProps["parametrizedConcepts$"],
			PortfolioLabContextProps["pageSplit$"],
			PortfolioLabContextProps["decimals$"],
			PortfolioLabContextProps["compositionColumnState$"],
			PortfolioLabContextProps["compositionGroupBy$"],
			PortfolioLabContextProps["compositionProjection$"],
			PortfolioLabContextProps["selectedScenarioId$"],
			PortfolioLabContextProps["metricsStatus$"],
		]
	>(() => [
		atomWithCompare(props.investments),
		atomWithCompare(props.charts),
		atomWithCompare(props.parametrizedConcepts),
		atomWithCompare(props.pageSplit),
		atom(props.decimals),
		// Seed from persisted column layout; `null` when none saved (grid reports its own state first).
		// `atomWithCompare` (deep-equal) so the grid's mount-time echo of the just-loaded layout is a
		// no-op write — it must not notify the save subscriber and trigger a redundant lab-session PUT
		// on every page load. Only a genuine layout change writes a different value and saves.
		atomWithCompare<ColumnState[] | null>(props.grid.columns.length > 0 ? props.grid.columns : null),
		// Seed the group-by from the persisted allocation preference.
		atom(props.grid.groupBy),
		// Carry the persisted projection through verbatim (see field doc).
		atom(props.grid.projection),
		// Seeded from the columns' own `scenarioId`: the backend stores the scenario per column, and this
		// atom is the single selection those columns share (see `scenarioIdFromColumns`).
		atom(props.selectedScenarioId),
		atomWithCompare<MetricsStatus>("init"),
	]);

	const undoRedo = useUndoRedo<LabHistory>();
	const [sessionSaveChain] = useState(makeLabSessionSaveChain);
	const [investmentWriteLanes] = useState(makeInvestmentWriteLanes);
	const [investmentComputes] = useState(makeInvestmentComputes);
	const [earlyComputeEvents] = useState(makeEarlyComputeEvents);

	// Every field is stable for the lifetime of the session (atoms and lanes from `useState`
	// initialisers, `undoRedo` memoised at its source), so the memo holds one object for the whole
	// session. Consumers subscribe to the atoms, not to this value; a fresh object here would drag
	// all 15 of them — the composition grid's per-row cell renderers included — into every render.
	// biome-ignore lint/correctness/useExhaustiveDependencies: the list names every field of the bundle, so a field added here cannot be left behind a stale memo
	const ctx = useMemo<PortfolioLabContextProps>(
		() => ({
			investments$,
			charts$,
			parametrizedConcepts$,
			pageSplit$,
			decimals$,
			compositionColumnState$,
			compositionGroupBy$,
			compositionProjection$,
			selectedScenarioId$,
			sessionSaveChain,
			investmentWriteLanes,
			investmentComputes,
			earlyComputeEvents,
			undoRedo,
			metricsStatus$,
		}),
		[
			investments$,
			charts$,
			parametrizedConcepts$,
			pageSplit$,
			decimals$,
			compositionColumnState$,
			compositionGroupBy$,
			compositionProjection$,
			selectedScenarioId$,
			sessionSaveChain,
			investmentWriteLanes,
			investmentComputes,
			earlyComputeEvents,
			undoRedo,
			metricsStatus$,
		],
	);

	return <PortfolioLabContext.Provider value={ctx}>{props.children}</PortfolioLabContext.Provider>;
}
